import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SchoolIcon from '@mui/icons-material/School';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupsIcon from '@mui/icons-material/Groups';
import LandingAppBar from './LandingAppBar';
import LandingFooter from './LandingFooter';

export default function ProductPage() {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const features = [
    {
      icon: SchoolIcon,
      title: 'AI-Powered Course Creation',
      description:
        'Create course wireframes in a matter of seconds from your Syllabus or lesson plan',
    },
    {
      icon: AutoFixHighIcon,
      title: 'Smart Automation',
      description:
        'Our course specific agents leverage RAG (Retrieval Augmented Generation) technology to maximize content relevance',
    },
    {
      icon: TimelineIcon,
      title: 'Syllabus-Centric Design',
      description:
        'The Syllabus is the heart and soul of the course, the single source of truth',
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <LandingAppBar />
      <main>
        {/* Hero Section */}
        <Box
          component="section"
          aria-label="Product Overview"
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            pt: { xs: 10, sm: 12 },
            pb: { xs: 8, sm: 10 },
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? `radial-gradient(ellipse 80% 60% at 50% -20%, rgba(91, 141, 239, 0.15), transparent)`
                : `radial-gradient(ellipse 80% 60% at 50% -20%, rgba(58, 108, 212, 0.10), transparent)`,
          }}
        >
          <Container maxWidth="md">
            <Stack spacing={4} alignItems="center" textAlign="center">
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: 'text.primary',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Introducing Syllabex
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1.05rem', sm: '1.2rem' },
                  color: 'text.secondary',
                  maxWidth: 600,
                  lineHeight: 1.7,
                }}
              >
                Less time spent on tedious tasks, more time spent doing what matters: Teaching. The result? Better learner outcomes.
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Features Grid */}
        <Box
          component="section"
          aria-label="Product Features"
          sx={{
            py: 1,
            bgcolor: 'background.default',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              Course specific AI Agents designed to automate the boilerplate
            </Typography>

            <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <Grid item xs={12} sm={6} md={6} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Paper
                      sx={{
                        p: 4,
                        height: '100%',
                        maxWidth: 320,
                        width: '100%',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          borderColor: 'primary.main',
                          boxShadow: (theme) =>
                            theme.palette.mode === 'dark'
                              ? '0 8px 24px rgba(91, 141, 239, 0.12)'
                              : '0 8px 24px rgba(58, 108, 212, 0.12)',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Stack spacing={2}>
                        <IconButton
                          disabled
                          sx={{
                            width: 56,
                            height: 56,
                            bgcolor: 'primary.main',
                            color: 'background.paper',
                            '&:disabled': {
                              bgcolor: 'primary.main',
                              color: 'background.paper',
                            },
                          }}
                        >
                          <IconComponent />
                        </IconButton>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        <Divider sx={{ my: { xs: 4, sm: 6, md: 8 } }} />

        {/* Screenshots/Demo Section */}
        <Box
          component="section"
          aria-label="Product Screenshots"
          sx={{
            py: { xs: 6, sm: 8, md: 10 },
            bgcolor: 'background.default',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 6,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              See It in Action
            </Typography>

            <Paper
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                aspectRatio: '16 / 9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
              }}
            >
              <Box
                component="video"
                src="/videos/paper-to-agent-demo.mp4"
                autoPlay
                loop
                muted
                playsInline
                aria-label="Demo video showing the Paper to Agent feature: uploading a paper, taking an aptitude assessment, and receiving a personalized learning path"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Paper>
          </Container>
        </Box>

        <Divider sx={{ my: { xs: 4, sm: 6, md: 8 } }} />

        {/* CTA Section */}
        <Box
          component="section"
          aria-label="Call to Action"
          sx={{
            py: { xs: 6, sm: 8, md: 10 },
            bgcolor: 'background.default',
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md">
            <Stack spacing={4} alignItems="center">
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2.25rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Ready to start teaching?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  color: 'text.secondary',
                  maxWidth: 500,
                }}
              >
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                }}
              >
                Join the Waitlist
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Scroll to Top Button */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 100,
          }}
        >
          <IconButton
            onClick={scrollToTop}
            sx={{
              bgcolor: 'primary.main',
              color: 'background.paper',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              width: 48,
              height: 48,
            }}
            aria-label="Scroll to top"
          >
            <ArrowUpwardIcon />
          </IconButton>
        </Box>
      </main>
      <LandingFooter />
    </Box>
  );
}
